import { CockpitFormSaveDialog } from '@components/cockpit/CockpitFormSaveDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { Breadcrumb } from '@components/cockpit/panels/Breadcrumb';
import ZoneFormMap from '@components/cockpit/panels/zoneManagement/form/ZoneFormMap';
import ZoneGeneralForm from '@components/cockpit/panels/zoneManagement/form/ZoneGeneralForm';
import {
  ZoneCodeAndType,
  ZoneForm,
  ZoneFormGeometryTypeEnum,
} from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveGroup, selectActiveSite } from '@redux/authent/authent.selectors';
import { selectZoneByCodeAndZoneType } from '@redux/config/config.selectors';
import {
  useCreateInitializationMaskingZoneMutation,
  useUpdateInitializationMaskingZoneMutation,
} from '@services/config/initializationMaskingZone.api';
import { useCreateNoFlyZoneMutation, useUpdateNoFlyZoneMutation } from '@services/config/noFlyZone.api';
import { useCreateNoInitZonesMutation, useUpdateNoInitZonesMutation } from '@services/config/noInitZone.api';
import { areSelectedFieldsEqual } from '@utils/common.utils';
import { isNoFlyZoneType } from '@utils/map/zone.utils';
import { zoneSchema } from '@validations/zone.schema';
import { Form, Formik } from 'formik';
import { Polygon } from 'geojson';
import { Dispatch, SetStateAction, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Mark, MarkEnum } from '@/types/commons/commons.types';
import {
  InitializationMaskingZone,
  NoFlyZone,
  NoInitZone,
  Zone2DTypeEnum,
  ZoneFormTypeEnum,
  ZoneToEditType,
  ZoneTypeEnum,
} from '@/types/config/config.types';
import { ProcedureModel } from '@/types/config/procedure.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';

function getInitialZone(
  editedZone: ZoneToEditType | null,
  type: ZoneFormTypeEnum | undefined,
  defaultPlatform: string | undefined,
): ZoneForm {
  const isNewZone = editedZone === null;

  const displayedFormat =
    !isNewZone && (editedZone.heightMin || editedZone.heightMax)
      ? ZoneFormDisplayedFormatEnum.HEIGHT
      : ZoneFormDisplayedFormatEnum.ALTITUDE;
  const editedLowerValue =
    (displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? editedZone?.altitudeMin : editedZone?.heightMin) ??
    null;
  const editedUpperValue =
    (displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? editedZone?.altitudeMax : editedZone?.heightMax) ??
    null;

  return {
    id: editedZone?.id,
    name: isNewZone ? '' : editedZone.name,
    type,
    platform:
      type && isNoFlyZoneType(type)
        ? (editedZone?.marks.find((mark) => mark.type === MarkEnum.PLATFORM)?.context ?? defaultPlatform)
        : undefined,
    geometry:
      editedZone?.minAltitudeFinite || editedZone?.maxAltitudeFinite
        ? ZoneFormGeometryTypeEnum.FINITE_HEIGHT
        : ZoneFormGeometryTypeEnum.INFINITE_HEIGHT,
    procedureModel: !isNewZone && type && isNoFlyZoneType(type) ? (editedZone as NoFlyZone).procedureModel : null,
    isZoneActive: isNewZone ? true : editedZone.active,
    displayedFormat,
    lowerValue: isNewZone ? 0 : editedLowerValue,
    upperValue: isNewZone ? 100 : editedUpperValue,
    marginSize: !isNewZone && 'marginSize' in editedZone ? editedZone.marginSize : 50,
    polygon: isNewZone ? { type: 'Polygon', coordinates: [] } : (editedZone.polygon as Polygon),
  };
}

const zoneFormTypeToAlertLevelEnum: Partial<Record<Zone2DTypeEnum, DroneAlertLevelEnum>> = {
  [Zone2DTypeEnum.NFZ_INFO]: DroneAlertLevelEnum.DRONE_INFO,
  [Zone2DTypeEnum.NFZ_WARNING]: DroneAlertLevelEnum.DRONE_WARNING,
  [Zone2DTypeEnum.NFZ_CRITICAL]: DroneAlertLevelEnum.DRONE_CRITICAL,
};

type Props = {
  editedZoneCodeAndType: ZoneCodeAndType | null | undefined;
  setEditedZoneCodeAndType: Dispatch<SetStateAction<ZoneCodeAndType | null | undefined>>;
};

export default function ZoneFormContainer({ editedZoneCodeAndType, setEditedZoneCodeAndType }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const [showMapForm, setShowMapForm] = useState(false);

  const activeSite = useAppSelector(selectActiveSite);
  const editedZone = useAppSelector((state) =>
    selectZoneByCodeAndZoneType(state, editedZoneCodeAndType ?? { code: '', type: Zone2DTypeEnum.NIZ }),
  );
  const activeGroup = useAppSelector(selectActiveGroup);
  const defaultPlatform = activeGroup?.defaultPlatform ?? undefined;

  const [createNIZ, { isLoading: isLoadingCreateNIZ }] = useCreateNoInitZonesMutation();
  const [createNFZ, { isLoading: isLoadingCreateNFZ }] = useCreateNoFlyZoneMutation();
  const [createIMZ, { isLoading: isLoadingCreateIMZ }] = useCreateInitializationMaskingZoneMutation();
  const isLoadingCreate = isLoadingCreateNIZ || isLoadingCreateNFZ || isLoadingCreateIMZ;

  const [updateNIZ, { isLoading: isLoadingUpdateNIZ }] = useUpdateNoInitZonesMutation();
  const [updateNFZ, { isLoading: isLoadingUpdateNFZ }] = useUpdateNoFlyZoneMutation();
  const [updateIMZ, { isLoading: isLoadingUpdateIMZ }] = useUpdateInitializationMaskingZoneMutation();
  const isLoadingUpdate = isLoadingUpdateNIZ || isLoadingUpdateNFZ || isLoadingUpdateIMZ;

  const isNewZone = editedZone === null;
  const validationSchema = useValidation(zoneSchema);

  function handleSuccess(name: string) {
    createToast(
      formatMessage({ id: editedZone ? 'components.zones.message.update' : 'components.zones.message.add' }, { name }),
      ToastStatusEnum.SUCCESS,
    );
    setEditedZoneCodeAndType(null);
  }

  function addSelectedPlatformToMarks(selected: string | undefined, actual: Mark[]): Mark[] {
    if (selected !== undefined && actual.some((mark) => mark.type === MarkEnum.PLATFORM && mark.context === selected)) {
      return actual;
    }
    const withoutPlatforms = actual.filter((mark) => mark.type !== MarkEnum.PLATFORM);
    if (selected === undefined) {
      return withoutPlatforms;
    }

    return [...withoutPlatforms, { id: null, type: MarkEnum.PLATFORM, context: selected }];
  }

  const saveZone = (
    type: ZoneFormTypeEnum | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dynamicZoneData: any,
    procedureModel: ProcedureModel | null,
    marginSize: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zoneCreateToSend: any,
  ) => {
    switch (type) {
      case Zone2DTypeEnum.NIZ:
        return editedZone
          ? updateNIZ({ id: editedZone.id, niz: { ...(editedZone as NoInitZone), ...dynamicZoneData } })
          : createNIZ({ ...zoneCreateToSend, type: ZoneTypeEnum.NIZ });
      case Zone2DTypeEnum.NFZ_INFO:
      case Zone2DTypeEnum.NFZ_WARNING:
      case Zone2DTypeEnum.NFZ_CRITICAL:
        return editedZone
          ? updateNFZ({
              id: editedZone.id,
              nfz: {
                ...(editedZone as NoFlyZone),
                ...dynamicZoneData,
                procedureModel: procedureModel!,
              },
            })
          : createNFZ({
              ...zoneCreateToSend,
              type: ZoneTypeEnum.NFZ,
              level: zoneFormTypeToAlertLevelEnum[type]!,
              procedureModel: procedureModel!,
            });
      case Zone2DTypeEnum.IMZ:
        return editedZone
          ? updateIMZ({
              id: editedZone.id,
              imz: { ...(editedZone as InitializationMaskingZone), ...dynamicZoneData, marginSize },
            })
          : createIMZ({ ...zoneCreateToSend, type: ZoneTypeEnum.IMZ, marginSize });
      default:
        return null;
    }
  };

  function submitZone({
    name,
    type,
    platform,
    procedureModel,
    geometry,
    isZoneActive,
    displayedFormat,
    lowerValue,
    upperValue,
    marginSize,
    polygon,
  }: ZoneForm) {
    const dynamicZoneData = {
      name,
      marks: addSelectedPlatformToMarks(platform, editedZone?.marks ?? []),
      active: isZoneActive,
      minAltitudeFinite: geometry === ZoneFormGeometryTypeEnum.FINITE_HEIGHT && !!lowerValue,
      maxAltitudeFinite: geometry === ZoneFormGeometryTypeEnum.FINITE_HEIGHT && !!upperValue,
      heightMin: displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT ? lowerValue : null,
      heightMax: displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT ? upperValue : null,
      altitudeMin: displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? lowerValue : null,
      altitudeMax: displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? upperValue : null,
      polygon,
    };

    const zoneCreateToSend = {
      ...dynamicZoneData,
      code: uuidv4(),
      site: activeSite === null ? 'local' : activeSite.code,
    };

    saveZone(type, dynamicZoneData, procedureModel, marginSize, zoneCreateToSend)
      ?.unwrap()
      .then(() => handleSuccess(name));
  }

  const zone = getInitialZone(editedZone, editedZoneCodeAndType?.type, defaultPlatform);

  const breadcrumbItems = [
    {
      label: formatMessage({ id: 'components.zones.title' }),
      action: () => setEditedZoneCodeAndType(null),
    },
    {
      label: isNewZone
        ? formatMessage({
            id: 'components.zones.form.newZone',
          })
        : zone.name,
      action: showMapForm ? () => setShowMapForm(false) : undefined,
    },
    ...(showMapForm
      ? [
          {
            label: formatMessage({ id: 'components.zones.form.volumeDefinition' }),
          },
        ]
      : []),
  ];

  return (
    <Formik
      initialValues={zone}
      validationSchema={validationSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={submitZone}
    >
      {({ initialValues, values, isValid, submitForm }) => {
        const hasChanged = () => {
          return !areSelectedFieldsEqual(values, initialValues, [
            'name',
            'platform',
            'mode',
            'procedureModel',
            'isZoneActive',
            'lowerValue',
            'upperValue',
            'marginSize',
            'polygon',
          ]);
        };

        return (
          <Form style={{ width: '100%', height: '100%' }} noValidate>
            <CockpitEditorLayout
              dialog={
                <CockpitFormSaveDialog
                  open={open}
                  isLoading={isLoadingCreate || isLoadingUpdate}
                  confirmLabel={formatMessage({ id: 'global.save' })}
                  cancelLabel={formatMessage({ id: 'global.cancel' })}
                  onConfirm={submitForm}
                  onCancel={() => setOpen(false)}
                  isNewElement={isNewZone}
                  name={values.name}
                  saveNewLabel="components.zones.popup.descriptionSaveNew"
                  saveUpdateLabel="components.zones.popup.descriptionSave"
                />
              }
              breadcrumbs={<Breadcrumb items={breadcrumbItems} />}
              onBack={() => setEditedZoneCodeAndType(null)}
              onSave={() => {
                if (!isValid || !hasChanged()) {
                  return;
                }
                setOpen(true);
              }}
              isSaving={isLoadingCreate || isLoadingUpdate}
              isSaveDisabled={!isValid || !hasChanged()}
            >
              {showMapForm ? (
                <ZoneFormMap />
              ) : (
                <ZoneGeneralForm isNewZone={isNewZone} setShowMapForm={setShowMapForm} />
              )}
            </CockpitEditorLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
